/*
 * SpellCheckMenu - Overlay-Menu fuer Rechtschreib-Vorschlaege.
 *
 * Erscheint anstelle des regulaeren ContextMenu, wenn der User mit der
 * rechten Maustaste auf ein Wort mit Warning klickt oder Cmd+. drueckt.
 * Routing-Logik liegt in mouseHandlers.js / keyboardHandlers.js — dieses
 * Modul ist nur das UI: Header (Wort) + bis zu 5 Vorschlaege + zwei
 * Aktionen (Add to personal / Ignore in project).
 *
 * Pattern wie js/UserMenu.js: Overlay zeichnet eine Box, setChar pro
 * Glyph, Keyboard-Nav + Click-Auswahl. Das Menue bleibt im Char-Grid —
 * das passt zum bestehenden Overlay-System (kein eigener Renderer-Pfad).
 */

import { Style } from '../config.js'
import { blurKeyboardSink } from '../touchKeyboard.js'

const MAX_LABEL_WIDTH = 36
const MAX_SUGGESTIONS = 5

export class SpellCheckMenu {
    /**
     * @param {import('../overlay.js').Overlay} overlay
     * @param {object} deps
     * @param {object} deps.editor                     - Carbisdale-Editor
     *        (mit `.model` und `.view`). Wird fuer das Replace gebraucht.
     * @param {object} deps.spellCheckManager          - SpellCheckManager
     *        fuer addPersonal()-Optimismus + warningAt-Lookups.
     * @param {Function} [deps.onAddPersonal]          - async (word) => void
     *        Cloud-Sync der Personal-Dict-Aenderung. Wird in Phase 2 nach
     *        dem Server-Persistenz-Schritt verdrahtet (PUT /api/user/spellcheck).
     * @param {Function} [deps.onIgnoreInProject]      - async (word) => void
     *        Cloud-Sync der Pro-Projekt-Ignore-Liste (Spec §7.4).
     */
    constructor(overlay, deps = {}) {
        this._overlay = overlay
        this._editor = deps.editor || null
        this._spellCheckManager = deps.spellCheckManager || null
        this._onAddPersonal = typeof deps.onAddPersonal === 'function'
            ? deps.onAddPersonal : null
        this._onIgnoreInProject = typeof deps.onIgnoreInProject === 'function'
            ? deps.onIgnoreInProject : null

        this.isOpen = false
        this._selectedIndex = 0
        this._items = []
        this._warning = null
        this._paragraphId = null
        this._field = null

        // Layout-Cache
        this._menuX = 0
        this._menuY = 0
        this._menuWidth = 0
        this._menuHeight = 0
        // Bildschirm-Dimensionen, gemerkt fuer Re-Renders nach Selection-Move
        this._cols = 0
        this._rows = 0
    }

    /**
     * Oeffnet das Spellcheck-Menue.
     *
     * Vor-Bedingung (vom Caller sichergestellt): Der Cursor steht im
     * richtigen Pane/Field, damit `Replace` die Selection im richtigen
     * Feld setzt.
     *
     * @param {object} payload
     * @param {{start:number, length:number, word:string, suggestions:string[]}} payload.warning
     * @param {string} payload.paragraphId
     * @param {'text'|'note'|'info'} payload.field
     * @param {number} anchorCharX  - Char-Spalte fuer die Menue-Position
     * @param {number} anchorCharY  - Char-Zeile fuer die Menue-Position
     * @param {number} cols         - Bildschirm-Spalten (zum Klemmen)
     * @param {number} rows         - Bildschirm-Zeilen (zum Klemmen)
     */
    show(payload, anchorCharX, anchorCharY, cols, rows) {
        // Phase 6.0d: Soft-Keyboard schliessen, falls offen
        blurKeyboardSink()
        this.isOpen = true
        this._selectedIndex = 0
        this._warning = payload.warning
        this._paragraphId = payload.paragraphId
        this._field = payload.field || 'text'

        this._items = this._buildItems(payload.warning)
        // Erstes auswaehlbares Item finden — bei "No suggestions" ist
        // der erste Eintrag ein Info-Header und nicht selektierbar.
        this._selectedIndex = this._firstSelectableIndex()
        this._cols = cols
        this._rows = rows
        this._render(anchorCharX, anchorCharY)
    }

    /** Schliesst das Menue. */
    close() {
        if (!this.isOpen) return
        this.isOpen = false
        this._overlay.deactivate()
        this._warning = null
        this._paragraphId = null
        this._field = null
        this._items = []
    }

    // ============================================================
    // Items
    // ============================================================

    _buildItems(warning) {
        const items = []
        // Header: das beanstandete Wort (informativ, nicht klickbar)
        items.push({ kind: 'header', label: this._truncate(warning.word) })
        items.push({ kind: 'separator' })

        const suggestions = (warning.suggestions || []).slice(0, MAX_SUGGESTIONS)
        if (suggestions.length === 0) {
            items.push({ kind: 'info', label: 'No suggestions' })
        } else {
            for (const sug of suggestions) {
                // Suggestions haben kein [K]-Hotkey — variable Anzahl, gewaehlt
                // wird per Hover/Klick oder Pfeil+Enter.
                items.push({
                    kind: 'item',
                    label: this._truncate(sug),
                    action: 'replace',
                    value: sug
                })
            }
        }
        items.push({ kind: 'separator' })
        // Aktionen mit [K]: A = Add to personal, I = Ignore in project
        items.push({ kind: 'item', key: 'A', label: 'Add to personal dictionary', action: 'addPersonal' })
        items.push({ kind: 'item', key: 'I', label: 'Ignore in this project', action: 'ignoreInProject' })
        return items
    }

    _firstSelectableIndex() {
        for (let i = 0; i < this._items.length; i++) {
            if (this._isSelectable(this._items[i])) return i
        }
        return 0
    }

    _isSelectable(item) {
        return item && item.kind === 'item'
    }

    _truncate(text) {
        if (text.length <= MAX_LABEL_WIDTH) return text
        return text.slice(0, MAX_LABEL_WIDTH - 1) + '…'
    }

    // ============================================================
    // Render
    // ============================================================

    _render(anchorCharX, anchorCharY) {
        this._overlay.clear()

        // Breite-Reservierung:
        //   - items mit [K]: 4 Chars fuer Bracket+Key+Space + Label
        //   - items ohne key (Suggestions): 1 Char Inset + Label
        //   - Header/Info: 1 Char Inset + Label (gerendert mit itemX + 1)
        //   - Separator: 0 (volle Linie, kein Label)
        const itemLengths = this._items.map(it => {
            const labelLen = (it.label || '').length
            if (it.kind === 'item' && it.key) return labelLen + 4
            if (it.kind === 'item') return labelLen + 1
            if (it.kind === 'header' || it.kind === 'info') return labelLen + 1
            return labelLen
        })
        const maxLen = Math.max(24, ...itemLengths)
        const width = Math.min(maxLen + 4, MAX_LABEL_WIDTH + 4)
        const height = this._items.length + 2  // + 2 fuer Box-Raender

        // Position: an Anchor klemmen, falls am Rand zu wenig Platz ist
        let x = anchorCharX
        let y = anchorCharY
        if (x + width > this._cols) x = Math.max(0, this._cols - width)
        if (y + height > this._rows) y = Math.max(0, this._rows - height)
        if (x < 0) x = 0
        if (y < 0) y = 0

        this._menuX = x
        this._menuY = y
        this._menuWidth = width
        this._menuHeight = height

        // Frame via neue Primitive (Border-Path + BG-Rect)
        this._overlay.drawMenuFrame(
            { x, y, width, height },
            { borderStyle: Style.MENU_BORDER, bgStyle: Style.MENU_BACKGROUND }
        )

        const itemX = x + 1
        const itemWidth = width - 2

        for (let i = 0; i < this._items.length; i++) {
            const item = this._items[i]
            const row = y + 1 + i

            if (item.kind === 'separator') {
                for (let c = 1; c < width - 1; c++) {
                    this._overlay.setChar(x + c, row, '─', Style.MENU_BORDER)
                }
                continue
            }

            if (item.kind === 'header') {
                // Header (das beanstandete Wort): proportionaler Title-Span,
                // 1 Cell Padding wie bei Project-Statistics-Header.
                this._overlay.addTextSpan(itemX + 1, row, item.label, Style.PANE_TITLE, itemWidth - 2)
                continue
            }

            if (item.kind === 'info') {
                // "No suggestions" — gedimmt, nicht selektierbar
                this._overlay.addTextSpan(itemX + 1, row, item.label, Style.PANE_INFO, itemWidth - 2)
                continue
            }

            // kind === 'item': normales Menue-Item via drawMenuItem.
            // Suggestions haben kein key — sie bekommen 1 Cell Inset, damit
            // ihre Labels mit dem Header-Wort fluchten (das ueber addTextSpan
            // mit +1 gerendert wird). Action-Items mit [K] bleiben auf itemX,
            // weil die Klammer optisch wie ein Marker wirkt.
            const isSelected = i === this._selectedIndex
            const hasKey = !!item.key
            const xPos = hasKey ? itemX : itemX + 1
            const w = hasKey ? itemWidth : itemWidth - 1
            this._overlay.drawMenuItem(
                xPos, row,
                {
                    key: item.key,
                    label: item.label,
                    hasSubmenu: false,
                    isCurrent: false,
                    isDisabled: false
                },
                { selected: isSelected, itemWidth: w }
            )
        }

        this._overlay.isDirty = true
        this._overlay.isActive = true
    }

    // ============================================================
    // Keyboard
    // ============================================================

    /**
     * Keyboard-Handler.
     * @param {KeyboardEvent} e
     * @returns {boolean} true wenn das Event verarbeitet wurde
     */
    handleKeydown(e) {
        if (!this.isOpen) return false

        switch (e.key) {
            case 'Escape':
                e.preventDefault()
                this.close()
                return true

            case 'ArrowUp':
                e.preventDefault()
                this._moveSelection(-1)
                return true

            case 'ArrowDown':
                e.preventDefault()
                this._moveSelection(+1)
                return true

            case 'Enter': {
                e.preventDefault()
                const item = this._items[this._selectedIndex]
                if (item && item.action) this._executeAction(item.action, item.value)
                return true
            }

            default: {
                // [K]-Hotkey: ein-Zeichen-Tasten gegen item.key matchen.
                // Nur die unteren Aktionen (Add to personal / Ignore) haben
                // keys; Suggestions werden ueber Pfeil+Enter oder Klick/Hover
                // ausgewaehlt.
                if (e.key.length === 1) {
                    const upper = e.key.toUpperCase()
                    const found = this._items.find(it =>
                        it.kind === 'item' && it.key === upper
                    )
                    if (found && found.action) {
                        e.preventDefault()
                        this._executeAction(found.action, found.value)
                        return true
                    }
                }
            }
        }
        return false
    }

    /**
     * Mouse-Move-Handler: Selektion folgt dem Mauszeiger ueber den
     * selektierbaren Items (Suggestions + Aktionen). Header/Separator/Info
     * werden uebersprungen.
     *
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn Pointer ueber dem Menue liegt
     */
    handleMouseMove(charX, charY) {
        if (!this.isOpen) return false

        const inside = charX >= this._menuX
            && charX < this._menuX + this._menuWidth
            && charY >= this._menuY
            && charY < this._menuY + this._menuHeight
        if (!inside) return false

        const itemIdx = charY - this._menuY - 1
        if (itemIdx < 0 || itemIdx >= this._items.length) return true
        const item = this._items[itemIdx]
        if (this._isSelectable(item) && itemIdx !== this._selectedIndex) {
            this._selectedIndex = itemIdx
            this._render(this._menuX, this._menuY)
        }
        return true
    }

    _moveSelection(delta) {
        if (this._items.length === 0) return
        let idx = this._selectedIndex
        for (let i = 0; i < this._items.length; i++) {
            idx = (idx + delta + this._items.length) % this._items.length
            if (this._isSelectable(this._items[idx])) {
                this._selectedIndex = idx
                // Re-Render an unveraenderter Position (Klemmung wurde beim
                // ersten show()-Call schon angewendet — _menuX/Y bleiben).
                this._render(this._menuX, this._menuY)
                return
            }
        }
    }

    // ============================================================
    // Click
    // ============================================================

    /**
     * Klick-Handler.
     * @param {number} charX
     * @param {number} charY
     * @returns {boolean} true wenn der Klick verarbeitet wurde
     */
    handleClick(charX, charY) {
        if (!this.isOpen) return false

        // Ausserhalb des Menues → schliessen, Klick fliesst zurueck
        if (charX < this._menuX || charX >= this._menuX + this._menuWidth ||
            charY < this._menuY || charY >= this._menuY + this._menuHeight) {
            this.close()
            return true
        }

        const itemIdx = charY - this._menuY - 1
        if (itemIdx < 0 || itemIdx >= this._items.length) return true
        const item = this._items[itemIdx]
        if (!this._isSelectable(item) || !item.action) return true
        this._executeAction(item.action, item.value)
        return true
    }

    // ============================================================
    // Actions
    // ============================================================

    _executeAction(action, value) {
        const warning = this._warning
        const paragraphId = this._paragraphId
        const field = this._field
        this.close()
        if (!warning || !paragraphId) return

        switch (action) {
            case 'replace':
                this._replaceWord(paragraphId, field, warning, value)
                break
            case 'addPersonal':
                this._addPersonal(warning.word)
                break
            case 'ignoreInProject':
                this._ignoreInProject(warning.word)
                break
        }
    }

    /**
     * Ersetzt das beanstandete Wort durch den Vorschlag. Nutzt
     * `replaceTextRange` — eine atomare Replace-Operation mit genau einem
     * Undo-Eintrag (sonst wuerde Cmd+Z die Korrektur in zwei Schritten
     * rueckgaengig machen: erst Insert weg, dann Delete-Re-Insert).
     *
     * @param {string} paragraphId
     * @param {string} field
     * @param {{start:number, length:number}} warning
     * @param {string} suggestion
     */
    _replaceWord(paragraphId, field, warning, suggestion) {
        if (!this._editor || !this._editor.model) return
        const model = this._editor.model
        const start = warning.start
        const end = warning.start + warning.length

        model.replaceTextRange(paragraphId, field, start, end, suggestion)

        // Suppression-Snapshot zuruecksetzen — sonst wuerde die naechste
        // Welle (falls die Korrektur immer noch falsch ist) direkt unter-
        // drueckt, weil _lastEditedCursor exakt am Cursor liegt.
        if (this._editor.view) {
            this._editor.view._lastEditedCursor = null
            this._editor.view.markDirty()
        }
    }

    /**
     * Fuegt das Wort dem Personal-Dict hinzu (Cloud-persistent ueber
     * den optionalen Callback). Optimistisches In-Memory-Update entfernt
     * Wellen sofort fuer alle Vorkommen.
     *
     * @param {string} word
     */
    _addPersonal(word) {
        if (!this._spellCheckManager) return
        this._spellCheckManager.addPersonal(word)
        if (this._onAddPersonal) {
            Promise.resolve(this._onAddPersonal(word)).catch(err => {
                console.warn('Add to personal dictionary failed:', err && err.message)
            })
        }
    }

    /**
     * Ignoriert das Wort fuer das aktuelle Projekt. Cloud-Persistenz
     * laeuft ueber den optionalen Callback (Spec §7.4 — pro-Projekt
     * spellcheck_ignored Liste).
     *
     * @param {string} word
     */
    _ignoreInProject(word) {
        if (this._onIgnoreInProject) {
            Promise.resolve(this._onIgnoreInProject(word)).catch(err => {
                console.warn('Ignore in project failed:', err && err.message)
            })
        }
    }
}
